import { startOfMonth, startOfWeek } from 'date-fns'

import i18n from '../../../i18n'
import { DataTypeValue, GranularityValue } from '../types/Filters'
import { PlotType } from '../types/PlotType'

// There are exactly 10 chart colors available (utilsService.getRGBChartColorList()), so up to 10 markets
// can be rendered as their own datasets. With more than 10 markets, the top 9 get their own dataset and
// the rest are rolled up into a single "Other markets" dataset using the 10th color. This way the "Other"
// legend only appears when at least two markets are actually combined into it. (GR-1329)
export const MAX_SEPARATE_CHART_MARKETS = 10

export const resolveSeparateMarketCount = (marketCount: number): number =>
  marketCount > MAX_SEPARATE_CHART_MARKETS ? MAX_SEPARATE_CHART_MARKETS - 1 : marketCount

export const isDataTypeRevenueAndDownloadsRatio = (dataType: DataTypeValue) => {
  return !(dataType === DataTypeValue.Revenue || dataType === DataTypeValue.Downloads)
}

export const isPlotTypeCumulative = (plotType?: PlotType) => {
  return plotType === PlotType.CumulativeStacked || plotType === PlotType.CumulativeLine
}

export const isPlotTypeStacked = (plotType?: PlotType) => {
  return plotType === PlotType.CumulativeStacked || plotType === PlotType.Stacked
}

export const getDataTypeName = (dataType: DataTypeValue, granularity: GranularityValue) => {
  const averageLabel = () => (granularity !== GranularityValue.Day ? ` (${i18n.t('common:average_label')})` : '')

  switch (dataType) {
    case DataTypeValue.Revenue:
      return i18n.t('common:revenue_text')
    case DataTypeValue.Downloads:
      return i18n.t('common:downloads_text')
    case DataTypeValue.RevenueDownloadsRatioAllTime:
      return i18n.t('common:all_time_revenue_downloads_ratio') + averageLabel()
    case DataTypeValue.RevenueDownloadsRatio7Days:
      return i18n.t('common:revenue_downloads_ratio_days', { days: 7 }) + averageLabel()
    case DataTypeValue.RevenueDownloadsRatio30Days:
      return i18n.t('common:revenue_downloads_ratio_days', { days: 30 }) + averageLabel()
    case DataTypeValue.RevenueDownloadsRatio90Days:
      return i18n.t('common:revenue_downloads_ratio_days', { days: 90 }) + averageLabel()
  }
}

export const getDateBeginingOfWeek = (date: Date) => {
  return startOfWeek(date)
}

export const getDateBeginningOfMonth = (date: Date) => {
  return startOfMonth(date)
}

export const getTimestampByGranularity = (granularity: GranularityValue, date: Date) => {
  switch (granularity) {
    case GranularityValue.Day:
      return new Date(date.setHours(0, 0, 0, 0))
    case GranularityValue.Week:
      return getDateBeginingOfWeek(date)
    case GranularityValue.Month:
      return getDateBeginningOfMonth(date)
  }
}

export const resolveTimeunitByGranularity = (granularity: GranularityValue) => {
  switch (granularity) {
    case GranularityValue.Day:
      return 'day'
    case GranularityValue.Week:
      return 'week'
    case GranularityValue.Month:
      return 'month'
  }
}
